/**
 * Field Material Feedback Service
 *
 * Supabase persistence layer for field_material_feedback records.
 * All reads are org-scoped via RLS; all writes explicitly include
 * organization_id from getCurrentOrganizationId().
 *
 * Reference pattern: src/services/lead.service.ts
 */

import { supabase } from "@/lib/supabase";
import { getCurrentOrganizationId } from "@/helpers/auth.helpers";
import type {
  FieldMaterialFeedbackEvent,
  FieldMaterialIssueType,
  FieldMaterialSeverity,
} from "@/data/VendorPOContext";

// ---------------------------------------------------------------------------
// Mapper — DB row (snake_case) → TS type (camelCase)
// ---------------------------------------------------------------------------
function fromRow(row: Record<string, unknown>): FieldMaterialFeedbackEvent {
  return {
    id:                    row.id as string,
    jobId:                 row.job_id as string,
    createdAt:             row.created_at as string,
    actor:                 row.actor as string,
    issueType:             row.issue_type as FieldMaterialIssueType,
    severity:              row.severity as FieldMaterialSeverity,
    description:           row.description as string,
    ...(row.material_reference != null  ? { materialReference:      row.material_reference as string }  : {}),
    ...(row.phase != null               ? { phase:                   row.phase as string }               : {}),
    ...(row.photo_evidence != null      ? { photoEvidence:           row.photo_evidence as string[] }    : {}),
    resolved:              row.resolved as boolean,
    ...(row.resolved_at != null         ? { resolvedAt:              row.resolved_at as string }         : {}),
    ...(row.resolved_by != null         ? { resolvedBy:              row.resolved_by as string }         : {}),
    ...(row.resolution_notes != null    ? { resolutionNotes:         row.resolution_notes as string }    : {}),
    ...(row.canonical_po_id != null     ? { canonicalPoId:           row.canonical_po_id as string }     : {}),
    ...(row.lineage_ambiguous != null   ? { lineageAmbiguous:        row.lineage_ambiguous as boolean }  : {}),
    ...(row.lineage_ambiguous_reason != null
                                        ? { lineageAmbiguousReason:  row.lineage_ambiguous_reason as string } : {}),
  };
}

// ---------------------------------------------------------------------------
// Mapper — TS type (camelCase) → DB row (snake_case)
// organization_id is injected separately in each write so it is never missed.
// ---------------------------------------------------------------------------
function toRow(evt: FieldMaterialFeedbackEvent): Record<string, unknown> {
  return {
    id:                       evt.id,
    job_id:                   evt.jobId,
    created_at:               evt.createdAt,
    actor:                    evt.actor,
    issue_type:               evt.issueType,
    severity:                 evt.severity,
    description:              evt.description,
    material_reference:       evt.materialReference ?? null,
    phase:                    evt.phase ?? null,
    photo_evidence:           evt.photoEvidence ?? null,
    resolved:                 evt.resolved,
    resolved_at:              evt.resolvedAt ?? null,
    resolved_by:              evt.resolvedBy ?? null,
    resolution_notes:         evt.resolutionNotes ?? null,
    canonical_po_id:          evt.canonicalPoId ?? null,
    lineage_ambiguous:        evt.lineageAmbiguous ?? null,
    lineage_ambiguous_reason: evt.lineageAmbiguousReason ?? null,
  };
}

function toPatchRow(patch: Partial<FieldMaterialFeedbackEvent>): Record<string, unknown> {
  const row: Record<string, unknown> = {};
  if (patch.resolved           !== undefined) row.resolved            = patch.resolved;
  if (patch.resolvedAt         !== undefined) row.resolved_at         = patch.resolvedAt;
  if (patch.resolvedBy         !== undefined) row.resolved_by         = patch.resolvedBy;
  if (patch.resolutionNotes    !== undefined) row.resolution_notes    = patch.resolutionNotes;
  if (patch.issueType          !== undefined) row.issue_type          = patch.issueType;
  if (patch.severity           !== undefined) row.severity            = patch.severity;
  if (patch.description        !== undefined) row.description         = patch.description;
  if (patch.materialReference  !== undefined) row.material_reference  = patch.materialReference;
  if (patch.phase              !== undefined) row.phase               = patch.phase;
  if (patch.photoEvidence      !== undefined) row.photo_evidence      = patch.photoEvidence;
  if (patch.canonicalPoId      !== undefined) row.canonical_po_id     = patch.canonicalPoId;
  if (patch.lineageAmbiguous   !== undefined) row.lineage_ambiguous   = patch.lineageAmbiguous;
  if (patch.lineageAmbiguousReason !== undefined) {
    row.lineage_ambiguous_reason = patch.lineageAmbiguousReason;
  }
  return row;
}

// ---------------------------------------------------------------------------
// Service
// ---------------------------------------------------------------------------
export const FieldMaterialFeedbackService = {
  /**
   * Fetch all field_material_feedback records for the current org.
   * RLS handles org scoping; organization_id filter is belt-and-suspenders.
   */
  async fetchAll(): Promise<FieldMaterialFeedbackEvent[]> {
    const organizationId = await getCurrentOrganizationId();
    const { data, error } = await supabase
      .from("field_material_feedback")
      .select("*")
      .eq("organization_id", organizationId)
      .order("created_at", { ascending: true });

    if (error) {
      console.error("FieldMaterialFeedbackService.fetchAll error:", error);
      return [];
    }
    return (data ?? []).map((row) => fromRow(row as Record<string, unknown>));
  },

  /**
   * Insert a new feedback event.
   * organization_id is always injected here — never rely on the caller.
   */
  async insert(evt: FieldMaterialFeedbackEvent): Promise<void> {
    const organizationId = await getCurrentOrganizationId();
    const row = { ...toRow(evt), organization_id: organizationId };
    const { error } = await supabase
      .from("field_material_feedback")
      .insert(row);

    if (error) {
      console.error("FieldMaterialFeedbackService.insert error:", error);
      throw error;
    }
  },

  /**
   * Patch an existing feedback event (e.g. resolve it).
   * organization_id is included in the patch so RLS WITH CHECK passes.
   */
  async update(id: string, patch: Partial<FieldMaterialFeedbackEvent>): Promise<void> {
    const organizationId = await getCurrentOrganizationId();
    const row = { ...toPatchRow(patch), organization_id: organizationId };
    const { error } = await supabase
      .from("field_material_feedback")
      .update(row)
      .eq("id", id)
      .eq("organization_id", organizationId);

    if (error) {
      console.error("FieldMaterialFeedbackService.update error:", error);
      throw error;
    }
  },
};
